import { apiClient } from './client'
import type { ApiResponse } from './client'

export type GameCurriculum =
  | 'CORE_A'
  | 'CORE_B'
  | 'CORE_C'
  | 'PRIME_A'
  | 'PRIME_B'
  | 'PRIME_C'

/** @quebon/games registry의 게임 유형 */
export type GameCategory =
  | 'number'
  | 'shape'
  | 'transition'
  | 'casual'
  | 'racing'
  | 'defense'
  | 'shooting'
  | 'puzzle'
  | 'action'

export type UnlockCondition =
  | { type: 'ALWAYS' }
  | {
      type: 'LESSON_COMPLETE'
      curriculum?: GameCurriculum
      chapter: number
      lesson: number
      lessonTitle?: string
    }

/**
 * 카드 표시 데이터. 랭킹/점수는 게임 내부 UI가 담당하므로 카드엔 없음.
 * title/description/imageUrl/type은 FE가 registry로 보강.
 */
export interface GameWithStatus {
  id: string
  key: string
  title: string
  description?: string
  imageUrl: string
  type?: GameCategory
  unlockCondition: UnlockCondition
  isUnlocked: boolean
  unlockHint?: string
}

export interface LearnerGamesListResponse {
  items: GameWithStatus[]
}

export interface LearnerGameAccessResponse {
  key: string
  accessible: boolean
}

export interface SubmitScoreRequest {
  score: number
  /** 소요/생존 시간(초). lostTemple의 Farm remaining ms는 호스트에서 초로 변환한다. */
  time?: number | null
  /** 게임 모드(legacy sub). 없으면 생략 */
  sub?: string | null
}

/**
 * 게임 내부 랭킹 UI(setRankBox)가 읽는 형태 — legacy java parity.
 * 게임은 entry.user.nickname / entry.rank / entry.time(또는 score)만 읽는다.
 */
export interface GameRankingEntry {
  rank: number
  score: number
  time: number | null
  user: { nickname: string }
}

/** 게임 상태 응답과 저장 요청은 legacy와 같은 {data} envelope를 사용한다. */
export interface GameStatePayload {
  data: Record<string, unknown>
}

export interface GameRankingResponse {
  ranking: GameRankingEntry[]
  my: GameRankingEntry | null
}

export const learnerGamesApi = {
  list: () =>
    apiClient.get<ApiResponse<LearnerGamesListResponse>>(
      '/lc/learner/me/games',
    ),

  access: (key: string) =>
    apiClient.get<ApiResponse<LearnerGameAccessResponse>>(
      `/lc/learner/me/games/${encodeURIComponent(key)}/access`,
    ),

  submitScore: (key: string, body: SubmitScoreRequest) =>
    apiClient.post<ApiResponse<GameRankingEntry>>(
      `/lc/learner/me/games/${encodeURIComponent(key)}/score`,
      body,
    ),

  loadState: (key: string, params?: { sub?: string | null }) =>
    apiClient.get<ApiResponse<GameStatePayload>>(
      `/lc/learner/me/games/${encodeURIComponent(key)}/state`,
      { params },
    ),

  saveState: (key: string, body: GameStatePayload, params?: { sub?: string | null }) =>
    apiClient.put<ApiResponse<GameStatePayload>>(
      `/lc/learner/me/games/${encodeURIComponent(key)}/state`,
      body,
      { params },
    ),

  loadRanking: (
    key: string,
    params?: { sub?: string | null; count?: number },
  ) =>
    apiClient.get<ApiResponse<GameRankingResponse>>(
      `/lc/learner/me/games/${encodeURIComponent(key)}/rank`,
      { params },
    ),
}
